import { useSync } from "@/context/SyncContext";
import { WifiOff, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

// Slim, non-blocking status bar shown whenever there are offline/pending photos.
export function SyncBar({ dark = false }) {
  const { pendingCount, queuedCount, failedCount, syncing, progress, isOnline, justSynced } = useSync();

  if (!pendingCount && !justSynced) return null;

  const base = "w-full text-sm font-medium px-4 py-2 flex items-center justify-center gap-2 text-center";

  if (pendingCount === 0 && justSynced) {
    return (
      <div data-testid="sync-bar-synced" className={`${base} bg-[#DCFCE7] text-[#166534]`}>
        <CheckCircle2 size={16} /> All photos synced
      </div>
    );
  }

  if (syncing && progress.total > 0) {
    const pct = Math.round((progress.done / progress.total) * 100);
    return (
      <div data-testid="sync-bar-syncing" className={`${base} bg-[#DBEAFE] text-[#1E40AF] flex-col !items-stretch gap-1`}>
        <span className="flex items-center justify-center gap-2">
          <Loader2 size={15} className="animate-spin" /> Syncing photos… {progress.done} of {progress.total}
        </span>
        <span className="block h-1.5 rounded-full bg-white/60 overflow-hidden">
          <span className="block h-full bg-[#1E40AF] transition-all" style={{ width: `${pct}%` }} />
        </span>
      </div>
    );
  }

  if (failedCount > 0 && queuedCount === 0) {
    return (
      <div data-testid="sync-bar-failed" className={`${base} bg-[#FEE2E2] text-[#B91C1C]`}>
        <AlertTriangle size={16} /> {failedCount} photo{failedCount > 1 ? "s" : ""} need attention — open the room to retry
      </div>
    );
  }

  // Pending, waiting for connectivity
  return (
    <div data-testid="sync-bar-offline" className={`${base} ${dark ? "bg-amber-400/15 text-amber-200" : "bg-[#FEF3C7] text-[#92400E]"}`}>
      <WifiOff size={16} /> {queuedCount} photo{queuedCount > 1 ? "s" : ""} saved on this device — syncs while TenantSeal is open and online
      {!isOnline ? "" : ""}
    </div>
  );
}
